import { useState, useEffect, useCallback, useRef } from 'preact/hooks'
import { Loader2 } from 'lucide-preact'
import type { WebAssetListItemDto, WebAssetViewDto, AssetVersionViewDto, ApprovalCommentViewDto, SignOffDto } from '@/api/asset'
import { assetApi } from '@/api/asset'
import { useToast } from '@/hooks/useToast'
import { useAIChatContextUpdater } from '@/hooks'
import { BRAND_CLASSES } from '@/constants/theme'
import { useAuth } from '@/auth/useAuth'
import { useAssetSignalR } from '@/hooks/useAssetSignalR'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'
import { useSasUriRefresh } from '@/hooks/useSasUriRefresh'
import { isWebsterAssetType } from '@/components/webs/constants/assetMeta'
import { AssetReviewTopBar } from './AssetReviewTopBar'
import { AssetDocumentViewer } from './AssetDocumentViewer'
import { AssetReviewSidePanel } from './AssetReviewSidePanel'
import { useAssetReviewPermissions } from '@/hooks/useAssetReviewPermissions'
import { useSendTestEmail } from '@/hooks/useSendTestEmail'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { HtmlPreview } from '@/components/webs/landingPage/HtmlPreview'
import { EmailTemplatePreview } from '@/components/assets/EmailTemplatePreview'
import { EmailCopyPreview } from '@/components/assets/EmailCopyPreview'

interface AssetReviewOverlayProps {
  assets: WebAssetListItemDto[]
  initialIndex: number
  clientCompanyId: string
  workspaceId: string
  campaignId: string
  onClose: () => void
  onAssetUpdated?: () => void
  onAssetChange?: (assetId: string) => void
  onEditInWebster?: (assetId: string, assetType: string) => void
}

export function AssetReviewOverlay({
  assets,
  initialIndex,
  clientCompanyId,
  workspaceId,
  campaignId,
  onClose,
  onAssetUpdated,
  onAssetChange,
  onEditInWebster,
}: AssetReviewOverlayProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [assetDetail, setAssetDetail] = useState<WebAssetViewDto | null>(null)
  const [versions, setVersions] = useState<AssetVersionViewDto[]>([])
  const [comments, setComments] = useState<ApprovalCommentViewDto[]>([])
  const [selectedVersionNumber, setSelectedVersionNumber] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()
  const [viewerExpanded, setViewerExpanded] = useState(false)
  // LP HTML preview state (fetched via the resolving version-HTML endpoint — ORB-419)
  const [lpHtml, setLpHtml] = useState<string | null>(null)
  const [lpThankYouHtml, setLpThankYouHtml] = useState<string | null>(null)
  // Past-version HTML for the Email Template/Copy preview (null = preview the current draft live)
  const [emailVersionHtml, setEmailVersionHtml] = useState<string | null>(null)
  const { showSuccess, showError, showErrorUnless422 } = useToast()
  const { updateContext } = useAIChatContextUpdater()
  const { userProfile } = useAuth()

  const currentAsset = assets[currentIndex]

  // --- SignalR callbacks (stable via refs to avoid dependency cascades) ---

  const currentAssetRef = useRef(currentAsset)
  currentAssetRef.current = currentAsset
  const onAssetUpdatedRef = useRef(onAssetUpdated)
  onAssetUpdatedRef.current = onAssetUpdated

  const handleCommentAdded = useCallback((comment: ApprovalCommentViewDto) => {
    setComments(prev =>
      prev.some(c => c.CommentId === comment.CommentId) ? prev : [...prev, comment]
    )
    setAssetDetail(prev => prev ? { ...prev, ETag: comment.ETag } : prev)
  }, [])

  const handleUploadCompleted = useCallback((notification: { versionNumber: number }) => {
    showSuccess('Upload complete', `v${notification.versionNumber} is ready.`)
    if (currentAssetRef.current) loadAssetDataRef.current(currentAssetRef.current.Id)
    onAssetUpdatedRef.current?.()
  }, [showSuccess])

  const handleUploadFailed = useCallback((notification: { errorMessage?: string | null }) => {
    showError('Upload failed', notification.errorMessage || 'The file could not be processed from cloud storage.')
  }, [showError])

  const { subscribeToUploadStatus, preJoinForUpload } = useAssetSignalR({
    assetId: currentAsset?.Id,
    userId: userProfile?.Id,
    onCommentAdded: handleCommentAdded as (...args: unknown[]) => void,
    onUploadCompleted: handleUploadCompleted,
    onUploadFailed: handleUploadFailed,
  })

  // --- AI chat context ---

  useEffect(() => {
    if (currentAsset) {
      updateContext({
        campaignId,
        assetId: currentAsset.Id,
        location: ChatLocations.WebAssetEditor,
        entityType: ChatEntityTypes.Asset,
        entityId: currentAsset.Id,
        viewMode: ChatViewModes.View,
        availableFields: [],
        availableActions: ['CopyToClipboard'],
      })
    }
  }, [workspaceId, campaignId, currentAsset?.Id, updateContext])

  // --- Data loading ---

  const loadAssetData = useCallback(async (assetId: string) => {
    setLoading(true)
    setError(undefined)
    setAssetDetail(null)
    setVersions([])
    setComments([])

    const [detailRes, versionsRes, commentsRes] = await Promise.all([
      assetApi.getAssetDetail(clientCompanyId, workspaceId, campaignId, assetId),
      assetApi.getAssetVersions(clientCompanyId, workspaceId, assetId),
      assetApi.getAssetComments(clientCompanyId, workspaceId, campaignId, assetId),
    ])

    if (detailRes.data) {
      setAssetDetail(detailRes.data)
      setSelectedVersionNumber(detailRes.data.CurrentVersionNumber)
    } else if (detailRes.error) {
      setError(detailRes.error)
    }

    if (versionsRes.data) {
      setVersions(versionsRes.data)

      // Re-subscribe for upload result if there's an in-progress URL upload
      const pendingVersion = versionsRes.data.find(
        v => v.UploadStatus === 'Pending' || v.UploadStatus === 'Downloading'
      )
      if (pendingVersion) {
        subscribeToUploadStatus(pendingVersion.Id)
      }
    }

    if (commentsRes.data) {
      setComments(commentsRes.data)
    }

    setLoading(false)
  }, [clientCompanyId, workspaceId, campaignId, subscribeToUploadStatus])

  const loadAssetDataRef = useRef(loadAssetData)
  loadAssetDataRef.current = loadAssetData

  useEffect(() => {
    if (currentAsset) {
      loadAssetDataRef.current(currentAsset.Id)
    }
  }, [currentAsset?.Id])

  // --- Load LP HTML for the selected promoted Landing Page version ---
  // ORB-419: route through the API (not a direct SAS blob fetch) so the event-display tokens and the
  // event-runtime sentinel resolve against the Web's current online-event timing. The stored version
  // blob stays tokenized because deploy substitutes from it; resolving on read mirrors the editor's
  // /Draft preview. The old direct-blob fetch bypassed the resolver and showed raw {{ORBITALX_EVENT_*}}
  // placeholders + an inert countdown for online-event pages.
  useEffect(() => {
    if (!assetDetail || assetDetail.Type !== 'Landing Page') return
    // Clear stale HTML up front — even when we skip the fetch below — so a versionless LP (or the brief
    // selectedVersionNumber===0 window while paging between assets) never shows the previous page.
    setLpHtml(null)
    setLpThankYouHtml(null)
    if (selectedVersionNumber <= 0) return
    let cancelled = false
    assetApi.getLandingPageVersionHtml(clientCompanyId, workspaceId, campaignId, assetDetail.Id, selectedVersionNumber)
      .then(res => {
        if (cancelled) return
        if (res.data) {
          setLpHtml(res.data.Html)
          setLpThankYouHtml(res.data.ThankYouHtml)
        } else if (res.error) {
          // A lapsed token or transient failure leaves the preview blank rather than silently stale;
          // log (matching the email version-preview path) instead of swallowing.
          console.error('[AssetReview] landing page version HTML load failed:', res.error)
        }
      })
      .catch(err => console.error('[AssetReview] landing page version HTML fetch failed', err))
    return () => { cancelled = true }
  }, [assetDetail?.Type, assetDetail?.Id, selectedVersionNumber, clientCompanyId, workspaceId, campaignId])

  // --- Load a past Email Template/Copy version's stored HTML for preview ---
  // The current version previews live (override = null); a past version is rendered through the
  // same substituting preview endpoint by passing its stored HTML as an override (ORB-301).
  useEffect(() => {
    const type = assetDetail?.Type
    if (type !== 'Email Template' && type !== 'Email Copy') return
    const isCurrentVersion = !assetDetail || selectedVersionNumber === assetDetail.CurrentVersionNumber
    if (isCurrentVersion) {
      setEmailVersionHtml(null)
      return
    }
    const version = versions.find(v => v.VersionNumber === selectedVersionNumber)
    // Both email types store the rendered HTML in RenderedHtmlBlobPath (BlobUrl is the design JSON).
    const blobUrl = version?.RenderedHtmlBlobUrl
    let cancelled = false
    setEmailVersionHtml(null)
    if (blobUrl) {
      fetch(blobUrl)
        .then(r => r.ok ? r.text() : null)
        .then(text => { if (!cancelled && text != null) setEmailVersionHtml(text) })
        .catch(err => {
          // A lapsed SAS URI surfaces as a fetch failure here; useSasUriRefresh re-fetches the
          // versions with fresh URIs, so this is recoverable — log at debug rather than swallow.
          console.debug('[AssetReview] version preview HTML fetch failed (SAS may have expired)', err)
        })
    }
    return () => { cancelled = true }
  }, [assetDetail?.Type, assetDetail?.CurrentVersionNumber, selectedVersionNumber, versions])

  // --- SAS URI refresh (silent — no loading state reset) ---

  const refreshSasUris = useCallback(async () => {
    if (!currentAsset) return
    const [detailRes, versionsRes] = await Promise.all([
      assetApi.getAssetDetail(clientCompanyId, workspaceId, campaignId, currentAsset.Id),
      assetApi.getAssetVersions(clientCompanyId, workspaceId, currentAsset.Id),
    ])
    if (detailRes.data) {
      setAssetDetail(prev => prev ? { ...prev, ...detailRes.data! } : detailRes.data!)
    }
    if (versionsRes.data) {
      setVersions(versionsRes.data)
    }
  }, [clientCompanyId, workspaceId, campaignId, currentAsset?.Id])

  // ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Lock body scroll + hide AI chat widget
  useBodyScrollLock()

  // --- Handlers ---

  const handlePrevious = currentIndex > 0
    ? () => {
        const newIndex = currentIndex - 1
        setCurrentIndex(newIndex)
        onAssetChange?.(assets[newIndex].Id)
      }
    : null

  const handleNext = currentIndex < assets.length - 1
    ? () => {
        const newIndex = currentIndex + 1
        setCurrentIndex(newIndex)
        onAssetChange?.(assets[newIndex].Id)
      }
    : null

  const handleVersionChange = (versionNumber: number) => {
    setSelectedVersionNumber(versionNumber)
  }

  const handleToggleExpand = useCallback(() => {
    setViewerExpanded(prev => !prev)
  }, [])

  const handleSignOff = async (comment?: string) => {
    if (!currentAsset || !assetDetail) return
    const dto: SignOffDto = {
      ETag: assetDetail.ETag,
      Confirmed: true,
      SignOffComment: comment,
    }
    const response = await assetApi.signOff(clientCompanyId, workspaceId, campaignId, currentAsset.Id, dto)
    if (response.error) {
      showErrorUnless422('Sign-off failed', response)
      return
    }
    if (response.data) {
      setAssetDetail(response.data)
      showSuccess('Signed off', `${response.data.Name} has been signed off.`)
      onAssetUpdated?.()
    }
  }

  const handleAddComment = async (text: string, parentCommentId?: string) => {
    if (!currentAsset || !assetDetail) return
    const response = await assetApi.addComment(clientCompanyId, workspaceId, campaignId, currentAsset.Id, {
      ETag: assetDetail.ETag,
      Text: text,
      VersionNumber: selectedVersionNumber,
      ParentCommentId: parentCommentId,
    })
    if (response.error) {
      showErrorUnless422('Comment failed', response)
      return
    }
    if (response.data) {
      setComments(prev =>
        prev.some(c => c.CommentId === response.data!.CommentId) ? prev : [...prev, response.data!]
      )
      setAssetDetail(prev => prev ? { ...prev, ETag: response.data!.ETag } : prev)
    }
  }

  const handleUploadVersion = async (file: File, versionComment?: string) => {
    if (!currentAsset || !assetDetail) return
    const response = await assetApi.uploadVersion(clientCompanyId, workspaceId, currentAsset.Id, {
      ETag: assetDetail.ETag,
      AssetId: currentAsset.Id,
      CampaignId: campaignId,
      WorkspaceId: workspaceId,
      FileName: file.name,
      MimeType: file.type || 'application/octet-stream',
      FileSizeBytes: file.size,
      VersionComment: versionComment,
    }, file)
    if (response.error) {
      showErrorUnless422('Upload failed', response)
      return
    }
    if (response.data) {
      setVersions(prev => [...prev, response.data!])
      setSelectedVersionNumber(response.data.VersionNumber)
      if (response.data.ParentAssetETag) {
        setAssetDetail(prev => prev ? { ...prev, ETag: response.data!.ParentAssetETag! } : prev)
      }
      showSuccess('Version uploaded', `v${response.data.VersionNumber} has been uploaded.`)
      onAssetUpdated?.()
    }
  }

  const handleDeleteAsset = async () => {
    if (!currentAsset) return
    const response = await assetApi.deleteAsset(clientCompanyId, workspaceId, campaignId, currentAsset.Id)
    if (response.error) {
      showErrorUnless422('Delete failed', response)
      throw response.error
    }
    showSuccess('Asset deleted', `${currentAsset.Name} has been deleted.`)
    onAssetUpdated?.()
    onClose()
  }

  const handleUploadVersionByCloudStorage = async (fileId: string, fileName: string, mimeType: string, provider: string, versionComment?: string) => {
    if (!currentAsset || !assetDetail || !userProfile?.Id) return

    // Pre-join SignalR group BEFORE the upload API call to avoid missing
    // fast completion notifications (race condition prevention)
    const cleanup = await preJoinForUpload('')

    const response = await assetApi.uploadVersionByCloudStorage(clientCompanyId, workspaceId, currentAsset.Id, {
      ETag: assetDetail.ETag,
      AssetId: currentAsset.Id,
      CampaignId: campaignId,
      WorkspaceId: workspaceId,
      CloudStorageFileId: fileId,
      CloudStorageFileName: fileName,
      CloudStorageMimeType: mimeType,
      CloudStorageProvider: provider,
      VersionComment: versionComment,
    })

    if (response.error) {
      cleanup() // Clean up SignalR subscription on failure
      showErrorUnless422('Upload failed', response)
      return
    }
    if (response.data) {
      setVersions(prev => [...prev, response.data!])
      setSelectedVersionNumber(response.data.VersionNumber)
      if (response.data.ParentAssetETag) {
        setAssetDetail(prev => prev ? { ...prev, ETag: response.data!.ParentAssetETag! } : prev)
      }

      // Now subscribe for this specific version's upload status
      subscribeToUploadStatus(response.data.Id)
    }
  }

  const handleUpdateAsset = async (updates: { Status?: string; Name?: string }) => {
    if (!currentAsset || !assetDetail) return
    const response = await assetApi.updateAsset(clientCompanyId, workspaceId, campaignId, currentAsset.Id, {
      Id: currentAsset.Id,
      ETag: assetDetail.ETag,
      CampaignId: campaignId,
      ...updates,
    })
    if (response.status === 409) {
      showError('Conflict', 'This asset was modified elsewhere. Refreshing...')
      await loadAssetData(currentAsset.Id)
      throw new Error('ETag conflict')
    }
    if (response.error) {
      showErrorUnless422('Update failed', response)
      throw new Error(response.error)
    }
    if (response.data) {
      // Merge metadata but preserve existing SAS URIs to avoid unnecessary viewer reloads
      setAssetDetail(prev => {
        if (!prev) return response.data!
        return {
          ...response.data!,
          CurrentVersionBlobUrl: prev.CurrentVersionBlobUrl,
          CurrentVersionBlobUrlExpiresInSeconds: prev.CurrentVersionBlobUrlExpiresInSeconds,
        }
      })
      const label = updates.Status ? `Status changed to ${response.data.Status}` : `Name updated`
      showSuccess('Asset updated', label)
      onAssetUpdated?.()
    }
  }

  const permissions = useAssetReviewPermissions()

  // Send Test (ORB-441) — the same Email Copy self-test as the builder, surfaced on the
  // review screen so a reviewer can mail themselves exactly what the preview shows before
  // signing off. Gated on canEdit (= server IsWorkspaceEditor, 1:1 with sign-off): builders
  // and designated approvers get it, plain viewers never do.
  const { canEdit } = useWorkspaceCapabilities(clientCompanyId, workspaceId)
  const { sendTest, sendingTest } = useSendTestEmail()

  // Send exactly what the preview renders: a past version's stored HTML (emailVersionHtml),
  // or the live draft (undefined override) for the current version. A past version's HTML
  // loads async (blob fetch) and is null while in flight — hold the action until it's in
  // hand so the send matches the selected version rather than the current draft.
  const isCurrentVersion = !assetDetail || selectedVersionNumber === assetDetail.CurrentVersionNumber
  const sendTestReady = isCurrentVersion || emailVersionHtml != null
  const canSendTest = assetDetail?.Type === 'Email Copy' && assetDetail?.Status !== 'Draft' && canEdit
  const handleSendTest = useCallback(async () => {
    if (!assetDetail) return
    // Guard the in-flight window: for a past version, only send once its HTML has loaded.
    if (selectedVersionNumber !== assetDetail.CurrentVersionNumber && emailVersionHtml == null) return
    await sendTest({
      clientCompanyId,
      workspaceId,
      campaignId,
      assetId: assetDetail.Id,
      body: { HtmlOverride: emailVersionHtml ?? undefined },
    })
  }, [sendTest, clientCompanyId, workspaceId, campaignId, assetDetail, emailVersionHtml, selectedVersionNumber])

  const canEditInWebster = permissions.canEditInWebster && assetDetail && isWebsterAssetType(assetDetail.Type) && !!onEditInWebster

  const handleEditInWebster = useCallback(() => {
    if (assetDetail && onEditInWebster) {
      onEditInWebster(assetDetail.Id, assetDetail.Type)
    }
  }, [assetDetail, onEditInWebster])

  // Determine which BlobUrl/fileName to show based on selected version
  const selectedVersion = versions.find(v => v.VersionNumber === selectedVersionNumber)
  const viewerBlobUrl = selectedVersion?.BlobUrl ?? assetDetail?.CurrentVersionBlobUrl ?? null
  const viewerFileName = selectedVersion?.OriginalFileName ?? assetDetail?.CurrentVersionFileName ?? null
  const viewerMimeType = selectedVersion?.MimeType ?? null

  // --- SAS URI auto-refresh before expiry ---
  const selectedVersionExpiry = selectedVersion?.BlobUrlExpiresInSeconds
  const detailExpiry = assetDetail?.CurrentVersionBlobUrlExpiresInSeconds
  const shortestExpiry = [selectedVersionExpiry, detailExpiry]
    .filter((v): v is number => v != null && v > 0)
    .reduce((min, v) => Math.min(min, v), Infinity)

  useSasUriRefresh(
    shortestExpiry === Infinity ? null : shortestExpiry,
    refreshSasUris
  )

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-slate-900">
      <AssetReviewTopBar
        onClose={onClose}
        onPrevious={handlePrevious}
        onNext={handleNext}
        currentIndex={currentIndex}
        totalAssets={assets.length}
        onEditInWebster={canEditInWebster ? handleEditInWebster : undefined}
        onSendTest={canSendTest ? handleSendTest : undefined}
        sendingTest={sendingTest}
        sendTestDisabled={!sendTestReady}
      />

      {error ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-red-600 dark:text-red-400 mb-2">{error}</p>
            <button
              onClick={() => currentAsset && loadAssetData(currentAsset.Id)}
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              Try again
            </button>
          </div>
        </div>
      ) : loading && !assetDetail ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
        </div>
      ) : assetDetail?.Status === 'Draft' ? (
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
              This asset is still being drafted and isn't available for review yet.
            </p>
            {canEditInWebster && (
              <button
                onClick={handleEditInWebster}
                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${BRAND_CLASSES.BUTTON.PRIMARY}`}
              >
                Open in builder
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {/* Left panel: LP preview, Email editors, or Document Viewer */}
          <div className="flex-1 min-w-0">
            {assetDetail?.Type === 'Landing Page' ? (
              <HtmlPreview
                html={lpHtml}
                thankYouHtml={lpThankYouHtml}
              />
            ) : assetDetail?.Type === 'Email Template' ? (
              // Server-rendered iframe instead of the editable Tiptap canvas — mirrors the
              // Email Copy preview pane so what reviewers see here matches what the recipient
              // gets in their inbox (table-based centering, sandboxed HTML, no editor quirks).
              <EmailTemplatePreview
                clientCompanyId={clientCompanyId}
                workspaceId={workspaceId}
                campaignId={campaignId}
                templateAssetId={assetDetail.Id}
                htmlOverride={emailVersionHtml}
              />
            ) : assetDetail?.Type === 'Email Copy' ? (
              // Read-only server-rendered preview — matches the live editor's preview
              // pane so reviewers see exactly what the recipient gets. Renders the current
              // draft live, or a selected past version's stored HTML (emailVersionHtml).
              <EmailCopyPreview
                clientCompanyId={clientCompanyId}
                workspaceId={workspaceId}
                campaignId={campaignId}
                assetId={assetDetail.Id}
                subject={assetDetail.Subject}
                subjectVariants={assetDetail.SubjectVariants}
                htmlOverride={emailVersionHtml}
              />
            ) : (
              <AssetDocumentViewer
                blobUrl={viewerBlobUrl}
                fileName={viewerFileName}
                mimeType={viewerMimeType}
                expanded={viewerExpanded}
                onToggleExpand={handleToggleExpand}
              />
            )}
          </div>

          {/* Side Panel */}
          {!viewerExpanded && <div className="w-[400px] xl:w-[440px] flex-shrink-0 border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            <AssetReviewSidePanel
              assetDetail={assetDetail}
              versions={versions}
              comments={comments}
              selectedVersionNumber={selectedVersionNumber}
              onVersionChange={handleVersionChange}
              onSignOff={handleSignOff}
              onAddComment={handleAddComment}
              onUploadVersion={handleUploadVersion}
              onUploadVersionByCloudStorage={handleUploadVersionByCloudStorage}
              onStatusChange={(status) => handleUpdateAsset({ Status: status })}
              onNameChange={(name) => handleUpdateAsset({ Name: name })}
              onDeleteAsset={handleDeleteAsset}
              loading={loading}
              permissions={permissions}
            />
          </div>}
        </div>
      )}
    </div>
  )
}
